// web-staking-estimator-frontend/src/utils/validation.js

/**
 * Format a value to a trimmed string
 */
export function fmt(v) {
  return (v ?? "").toString().trim();
}

/**
 * Check if a value has content (non-empty after trimming)
 */
export function has(v) {
  return !!fmt(v);
}

/**
 * Check if a member object has all required fields for basic validation
 * Returns an array of missing field names
 */
export function requiredMissing(member) {
  const m = member || {};
  const misses = [];
  
  if (!has(m.first_name)) misses.push("First name");
  if (!has(m.last_name)) misses.push("Last name");
  if (!has(m.phone)) misses.push("Telephone");
  else if (String(m.phone).replace(/\D/g, "").length !== 10) {
    // US phone numbers only - strip formatting and check for exactly 10 digits
    misses.push("Telephone (10 digits)");
  }
  
  return misses;
}

/**
 * Check required member info for estimate generation and service requests
 * Includes phone length validation
 * @param {object} member - Member information object
 * @returns {string[]} Array of missing field names (lowercase)
 */
export function missingMemberInfo(member) {
  const s = (x) => !!(x ?? "").toString().trim();
  const miss = [];
  
  if (!s(member?.first_name)) miss.push("first name");
  if (!s(member?.last_name)) miss.push("last name");
  if (!s(member?.phone)) miss.push("telephone");
  else if (String(member?.phone).replace(/\D/g, "").length !== 10) {
    miss.push("telephone (10 digits)");
  }
  
  return miss;
}

// Aliases for backward compatibility
export const missingForGenerateMember = missingMemberInfo;
export const missingForStartRequest = missingMemberInfo;

/**
 * Check required service request fields for estimate generation
 * @param {object} requestInfo - Service request information
 * @param {object} selectedPoint - Selected map point
 * @returns {string[]} Array of missing field descriptions
 */
export function missingForEstimate(requestInfo, selectedPoint) {
  const miss = [];
  
  if (!selectedPoint?.x || !selectedPoint?.y) {
    miss.push("map pin location");
  }
  
  if (!requestInfo?.matched_service_id) {
    miss.push("service configuration");
  }
  
  return miss;
}

/*
 * Validate irrigation horsepower against phase-specific limits
 * Business rule: Different phases support different max HP
 * 1-phase: typically 5-25 HP (smaller pumps, residential/small farms)
 * 3-phase: up to 100+ HP (large irrigation systems, commercial ag)
 * Config defines exact limits - we enforce them here before letting user submit
 */
export function validateIrrigationHP(hp, phase, config) {
  const hpNum = Number(hp);
  
  // Check if valid number
  if (!Number.isFinite(hpNum) || hpNum <= 0) {
    return { valid: false, error: 'Horsepower must be greater than 0' };
  }
  
  // Check decimal places (max 2) - irrigation pumps are rated like 7.5 HP, 15 HP
  const hpStr = String(hp);
  if (hpStr.includes('.')) {
    const decimals = hpStr.split('.')[1];
    if (decimals && decimals.length > 2) {
      return { valid: false, error: 'Horsepower limited to 2 decimal places' };
    }
  }
  
  const limits = config?.limits || {};
  const phaseNum = Number(phase);
  
  /*
   * Phase-specific HP limits
   * 1-phase infrastructure can't handle large motors (electrical/transformer capacity)
   * 3-phase can handle much larger loads
   * Limits come from backend config (admin configurable)
   */
  if (phaseNum === 1) {
    const maxHP = limits['1phase']?.max || 25;
    if (hpNum > maxHP) {
      return { 
        valid: false, 
        error: limits['1phase']?.message || `Maximum ${maxHP} HP for 1-phase`
      };
    }
  }
  
  if (phaseNum === 3) {
    const maxHP = limits['3phase']?.max || 100;
    if (hpNum > maxHP) {
      return { 
        valid: false, 
        error: limits['3phase']?.message || `Maximum ${maxHP} HP for 3-phase`
      };
    }
  }
  
  return { valid: true, error: null };
}
